import type { 
  TimesheetDeadlineSettings,
  UtilisationSettings,
  PolicyLimits,
  PolicyViolationSettings
} from '@/types/timesheetSettings';

export interface ValidationError {
  field: string;
  message: string;
  tab: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export const useSettingsValidation = () => {
  const validateDeadlineSettings = (settings: TimesheetDeadlineSettings): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (!settings.deadlineTime) {
      errors.push({
        field: 'deadlineTime',
        message: 'Deadline time is required',
        tab: 'submissions-deadlines'
      });
    }
    
    if (settings.gracePeriodDays < 0) {
      errors.push({
        field: 'gracePeriodDays',
        message: 'Grace period cannot be negative',
        tab: 'submissions-deadlines'
      });
    }
    
    if (settings.gracePeriodDays > 7) {
      errors.push({
        field: 'gracePeriodDays',
        message: 'Grace period cannot exceed 7 days',
        tab: 'submissions-deadlines'
      });
    }
    
    if (settings.sendReminders && settings.reminderDaysBefore < 1) {
      errors.push({
        field: 'reminderDaysBefore',
        message: 'Reminder days before must be at least 1 day',
        tab: 'submissions-deadlines'
      });
    }
    
    return errors;
  };
  
  const validateUtilisationSettings = (settings: UtilisationSettings): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (settings.targetUtilisationRate < 0 || settings.targetUtilisationRate > 1) {
      errors.push({
        field: 'targetUtilisationRate',
        message: 'Utilisation target must be between 0 and 1 (e.g., 0.75 for 75%)',
        tab: 'utilisation'
      });
    }
    
    return errors;
  };
  
  const validatePolicyLimits = (limits: PolicyLimits | undefined): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (!limits) return errors;
    
    if (limits.legalMaxHoursPerDay && limits.legalMaxHoursPerDay < 0) {
      errors.push({
        field: 'legalMaxHoursPerDay',
        message: 'Legal max hours per day cannot be negative',
        tab: 'working-hours'
      });
    }
    
    if (limits.legalMaxHoursPerWeek && limits.legalMaxHoursPerWeek < 0) {
      errors.push({
        field: 'legalMaxHoursPerWeek',
        message: 'Legal max hours per week cannot be negative',
        tab: 'working-hours'
      });
    }
    
    if (limits.legalMaxHoursPerDay && limits.legalMaxHoursPerWeek) {
      if (limits.legalMaxHoursPerDay * 7 < limits.legalMaxHoursPerWeek) {
        errors.push({
          field: 'legalMaxHoursPerWeek',
          message: 'Weekly limit should not exceed 7x daily limit',
          tab: 'working-hours'
        });
      }
    }
    
    return errors;
  };
  
  const validateTimeEntrySettings = (
    historicalEntryLimit: number,
    minimumEntryDuration: string,
    showRole: boolean,
    requireRole: boolean,
    showCostCentre: boolean,
    requireCostCentre: boolean
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (historicalEntryLimit < 1 || historicalEntryLimit > 52) {
      errors.push({
        field: 'historicalEntryLimit',
        message: 'Historical entry limit must be between 1 and 52 weeks',
        tab: 'time-entry'
      });
    }
    
    if (requireRole && !showRole) {
      errors.push({
        field: 'requireRole',
        message: 'Cannot require role when role field is hidden',
        tab: 'time-entry'
      });
    }
    
    if (requireCostCentre && !showCostCentre) {
      errors.push({
        field: 'requireCostCentre',
        message: 'Cannot require cost centre when cost centre field is hidden',
        tab: 'time-entry'
      });
    }
    
    return errors;
  };
  
  const validateAllSettings = (
    deadlineSettings: TimesheetDeadlineSettings | null,
    utilisationSettings: UtilisationSettings | null,
    policyLimits: PolicyLimits | undefined,
    historicalEntryLimit: number,
    minimumEntryDuration: string,
    showRole: boolean,
    requireRole: boolean,
    showCostCentre: boolean,
    requireCostCentre: boolean
  ): ValidationResult => {
    const allErrors: ValidationError[] = [];
    
    if (deadlineSettings) {
      allErrors.push(...validateDeadlineSettings(deadlineSettings));
    }
    
    if (utilisationSettings) {
      allErrors.push(...validateUtilisationSettings(utilisationSettings));
    }
    
    allErrors.push(...validatePolicyLimits(policyLimits));
    
    allErrors.push(...validateTimeEntrySettings(
      historicalEntryLimit,
      minimumEntryDuration,
      showRole,
      requireRole,
      showCostCentre,
      requireCostCentre
    ));
    
    return {
      isValid: allErrors.length === 0,
      errors: allErrors
    };
  };
  
  return {
    validateDeadlineSettings,
    validateUtilisationSettings,
    validatePolicyLimits,
    validateTimeEntrySettings,
    validateAllSettings
  };
};
